import React from "react";
import { Alert, ColorPicker, Divider, Input, InputNumber, Select, Slider, Space, Switch, Tooltip, Typography } from "antd";
import { Content } from "antd/es/layout/layout";
import {
  ESTILO_PADRAO,
  MARGENS_PADRAO,
  PALETA_TEXTO,
  ROTULO_PADRAO,
  calcularEscalaFonte,
  contarRotulosVisiveis,
} from "./tema";

const { Text } = Typography;
const PRESETS = [{ label: "Cores da casa", colors: PALETA_TEXTO.map((c) => c.cor) }];

const OPCOES_ROTULO = [
  { value: "todos", label: "Todos os pontos" },
  { value: "extremos", label: "Só o primeiro e o último" },
  { value: "nenhum", label: "Nenhum" },
];

/** Rótulo curto + controle, no mesmo alinhamento em toda a coluna. */
function Campo({ titulo, dica, children }) {
  const corpo = (
    <Content style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 32 }}>
      <Text style={{ fontSize: 12, width: 108, flexShrink: 0 }}>{titulo}</Text>
      {children}
    </Content>
  );
  return dica ? <Tooltip title={dica}>{corpo}</Tooltip> : corpo;
}

/**
 * Painel de estilo: expõe TODOS os campos visuais do gráfico.
 *
 * A ideia é que nada fique preso no código — cor, fundo, borda, opacidade,
 * espaçamento e tamanho de cada elemento são editáveis aqui, do mesmo jeito
 * que a cor do texto do título já era.
 */
export default function PainelEstilo({ spec, onChange }) {
  const estilo = { ...ESTILO_PADRAO, ...(spec.estilo || {}) };
  const rotulo = { ...ROTULO_PADRAO, ...(spec.rotulo || {}) };
  const legenda = spec.legenda || {};
  const valores = spec.valores || {};
  const fontes = spec.fontes || {};
  const eixo = spec.eixo || {};
  const margens = { ...MARGENS_PADRAO, ...(spec.margens || {}) };

  const mesclar = (chave, valor) => onChange({ [chave]: { ...(spec[chave] || {}), ...valor } });

  const escalaEfetiva = calcularEscalaFonte(spec);
  const nRotulos = contarRotulosVisiveis(spec);

  return (
    <Content style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {/* ---------------- visual ---------------- */}
      <Campo titulo="Visual">
        <Select
          style={{ width: 150 }}
          size="small"
          value={estilo.visual}
          onChange={(v) => mesclar("estilo", { visual: v })}
          options={[
            { value: "moderno", label: "Moderno (vidro)" },
            { value: "classico", label: "Clássico" },
          ]}
        />
      </Campo>

      {estilo.visual !== "classico" && (
        <>
          <Campo titulo="Translucidez" dica="Quanto o fundo das caixas de valor deixa passar o que está atrás.">
            <Slider
              style={{ flex: 1 }}
              min={0}
              max={1}
              step={0.05}
              value={estilo.translucidez}
              onChange={(v) => mesclar("estilo", { translucidez: v })}
            />
          </Campo>
          <Campo titulo="Cantos" dica="Raio dos cantos arredondados das caixas.">
            <Slider
              style={{ flex: 1 }}
              min={0}
              max={14}
              value={estilo.cantos}
              onChange={(v) => mesclar("estilo", { cantos: v })}
            />
          </Campo>
        </>
      )}

      {/* ---------------- área segura ---------------- */}
      <Campo
        titulo="Área segura"
        dica="Folga mínima entre o conteúdo e a borda da imagem, em pixels da imagem final (604x366). Aumente se algum valor estiver encostando ou saindo pela borda."
      >
        <Space size={4}>
          {[
            ["esquerda", "esq."],
            ["direita", "dir."],
            ["topo", "topo"],
            ["baixo", "baixo"],
          ].map(([lado, rotuloLado]) => (
            <Tooltip key={lado} title={rotuloLado}>
              <InputNumber
                size="small"
                style={{ width: 58 }}
                min={0}
                max={60}
                value={margens[lado]}
                onChange={(v) => mesclar("margens", { [lado]: v ?? 0 })}
              />
            </Tooltip>
          ))}
        </Space>
      </Campo>

      <Divider style={{ margin: "6px 0" }} />

      {/* ---------------- fontes ---------------- */}
      <Campo
        titulo="Fonte automática"
        dica="Reduz todas as fontes quando há muitos rótulos, para caberem sem se sobrepor."
      >
        <Switch
          size="small"
          checked={fontes.automatico !== false}
          onChange={(v) => mesclar("fontes", { automatico: v })}
        />
        <Text type="secondary" style={{ fontSize: 11 }}>
          {nRotulos} rótulo{nRotulos === 1 ? "" : "s"} → escala {escalaEfetiva.toFixed(2)}×
        </Text>
      </Campo>
      <Campo titulo="Ajuste manual" dica="Multiplica o tamanho de todas as fontes.">
        <Slider
          style={{ flex: 1 }}
          min={0.6}
          max={1.6}
          step={0.05}
          value={fontes.escala ?? 1}
          onChange={(v) => mesclar("fontes", { escala: v })}
        />
      </Campo>

      {/* Acima de ~14 caixas de valor nenhuma fonte legivel faz todas
          caberem lado a lado em 604px, e o Highcharts passa a esconder as
          que colidem — o que aparece como valores sumindo. Melhor dizer o
          que esta acontecendo e apontar a saida do que deixar o usuario
          achando que e defeito. */}
      {nRotulos > 14 && valores.exibirEm === "todos" && (
        <Alert
          type="warning"
          showIcon
          style={{ fontSize: 12 }}
          message="Muitos valores para o espaço"
          description={`São ${nRotulos} caixas de valor: as que se sobrepõem serão omitidas na imagem. Use "Mostrar valor em: só o primeiro e o último" ou reduza as categorias.`}
        />
      )}

      <Divider style={{ margin: "6px 0" }} />

      {/* ---------------- caixa de valor ---------------- */}
      <Text strong style={{ fontSize: 12 }}>
        Caixa do valor
      </Text>
      <Campo titulo="Cores">
        <Space size={4}>
          <Tooltip title="Texto">
            <ColorPicker
              size="small"
              value={rotulo.corTexto}
              presets={PRESETS}
              disabledAlpha
              onChange={(c) => mesclar("rotulo", { corTexto: c.toHexString() })}
            />
          </Tooltip>
          <Tooltip title="Fundo">
            <ColorPicker
              size="small"
              value={rotulo.corFundo}
              presets={PRESETS}
              disabledAlpha
              onChange={(c) => mesclar("rotulo", { corFundo: c.toHexString() })}
            />
          </Tooltip>
          <Tooltip title="Borda">
            <ColorPicker
              size="small"
              value={rotulo.corBorda}
              presets={PRESETS}
              disabledAlpha
              onChange={(c) => mesclar("rotulo", { corBorda: c.toHexString() })}
            />
          </Tooltip>
        </Space>
      </Campo>
      <Campo
        titulo="Opacidade"
        dica="Fundo da caixa de valor: 0 transparente, 1 sólido. Sem ajuste, usa a translucidez geral (moderno) ou sólido (clássico)."
      >
        <Slider
          style={{ flex: 1 }}
          min={0}
          max={1}
          step={0.05}
          value={rotulo.opacidade ?? (estilo.visual === "classico" ? 1 : estilo.translucidez)}
          onChange={(v) => mesclar("rotulo", { opacidade: v })}
        />
      </Campo>
      <Campo titulo="Espaço / borda">
        <Space size={4}>
          <InputNumber
            size="small"
            style={{ width: 62 }}
            min={0}
            max={12}
            value={rotulo.espaco ?? 3}
            onChange={(v) => mesclar("rotulo", { espaco: v ?? 3 })}
          />
          <InputNumber
            size="small"
            style={{ width: 62 }}
            min={0}
            max={4}
            value={rotulo.larguraBorda ?? 1}
            onChange={(v) => mesclar("rotulo", { larguraBorda: v ?? 1 })}
          />
        </Space>
      </Campo>
      <Campo
        titulo="Tamanho do valor"
        dica="Só o texto da caixa de valor, sem mexer no título, nas categorias e na legenda."
      >
        <Slider
          style={{ flex: 1 }}
          min={0.5}
          max={2}
          step={0.05}
          value={rotulo.tamanho ?? 1}
          onChange={(v) => mesclar("rotulo", { tamanho: v })}
        />
      </Campo>
      <Campo
        titulo="Posição do valor"
        dica="Sobe/desce e move para os lados as caixas de valor, em pixels da imagem final. Positivo sobe / vai para a direita."
      >
        <Space size={4}>
          <InputNumber
            size="small"
            style={{ width: 86 }}
            min={-60}
            max={60}
            addonBefore="↕"
            value={rotulo.deslocY ?? 0}
            onChange={(v) => mesclar("rotulo", { deslocY: v ?? 0 })}
          />
          <InputNumber
            size="small"
            style={{ width: 86 }}
            min={-60}
            max={60}
            addonBefore="↔"
            value={rotulo.deslocX ?? 0}
            onChange={(v) => mesclar("rotulo", { deslocX: v ?? 0 })}
          />
        </Space>
      </Campo>

      <Divider style={{ margin: "6px 0" }} />

      {/* ---------------- legenda e eixo ---------------- */}
      <Campo titulo="Legenda">
        <Switch
          size="small"
          checked={Boolean(legenda.visivel)}
          onChange={(v) => mesclar("legenda", { visivel: v })}
        />
        <Space size={4}>
          <Tooltip title="Texto da legenda">
            <ColorPicker
              size="small"
              value={legenda.corTexto || "#FFFFFF"}
              presets={PRESETS}
              disabledAlpha
              onChange={(c) => mesclar("legenda", { corTexto: c.toHexString() })}
            />
          </Tooltip>
          <Tooltip title="Pastilha da legenda">
            <ColorPicker
              size="small"
              value={legenda.corFundo || "#FFFFFF"}
              presets={PRESETS}
              disabledAlpha
              onChange={(c) => mesclar("legenda", { corFundo: c.toHexString() })}
            />
          </Tooltip>
        </Space>
        <Slider
          style={{ flex: 1 }}
          min={0}
          max={0.6}
          step={0.02}
          value={legenda.opacidade ?? 0.1}
          onChange={(v) => mesclar("legenda", { opacidade: v })}
        />
      </Campo>

      <Campo titulo="Eixo / categorias">
        <ColorPicker
          size="small"
          value={eixo.corTexto || "#FFFFFF"}
          presets={PRESETS}
          disabledAlpha
          onChange={(c) => mesclar("eixo", { corTexto: c.toHexString() })}
        />
        <Tooltip title="Mínimo do eixo. Vazio começa no zero; cortar a base destaca diferenças pequenas.">
          <InputNumber
            size="small"
            style={{ width: 110 }}
            placeholder="mín."
            value={eixo.min}
            onChange={(v) => mesclar("eixo", { min: v })}
          />
        </Tooltip>
        <InputNumber
          size="small"
          style={{ width: 110 }}
          placeholder="máx."
          value={eixo.max}
          onChange={(v) => mesclar("eixo", { max: v })}
        />
      </Campo>
      <Campo
        titulo="Texto das categorias"
        dica="Tamanho e posição (sobe/desce) do texto do eixo de categorias, à parte das outras fontes."
      >
        <Slider
          style={{ flex: 1 }}
          min={0.5}
          max={2}
          step={0.05}
          value={eixo.tamanhoCategoria ?? 1}
          onChange={(v) => mesclar("eixo", { tamanhoCategoria: v })}
        />
        <InputNumber
          size="small"
          style={{ width: 80 }}
          min={-40}
          max={40}
          addonBefore="↕"
          value={eixo.deslocCategoria ?? 0}
          onChange={(v) => mesclar("eixo", { deslocCategoria: v ?? 0 })}
        />
      </Campo>
      <Campo
        titulo="Variações"
        dica="Aperta ou abre o intervalo do eixo de valores. Acentuado: as linhas e barras ocupam a altura toda e as variações aparecem mais. Suave: sobra espaço e o desenho achata. Mínimo/máximo digitados acima continuam valendo."
      >
        <Slider
          style={{ flex: 1, marginInline: 14 }}
          min={-3}
          max={3}
          step={1}
          value={eixo.amplitude ?? 0}
          onChange={(v) => mesclar("eixo", { amplitude: v })}
          marks={{ "-3": "suave", 0: "auto", 3: "acentuado" }}
          included={false}
          tooltip={{
            formatter: (v) =>
              ({ "-3": "Bem suave", "-2": "Suave", "-1": "Um pouco suave", 0: "Automático", 1: "Um pouco acentuado", 2: "Acentuado", 3: "Bem acentuado" }[v]),
          }}
        />
      </Campo>
      <Campo
        titulo="Posição do gráfico"
        dica="Sobe ou desce o desenho (linhas/colunas junto com as caixas de valor) dentro da área do gráfico, sem mexer no título nem nas categorias. Em % da altura; positivo sobe. Linhas andam inteiras; colunas e áreas ficam com a base no lugar e só o topo sobe ou desce. Não se aplica a barras horizontais, rosca e funil."
      >
        <Slider
          style={{ flex: 1, marginInline: 14 }}
          min={-40}
          max={40}
          step={1}
          included={false}
          value={eixo.posicao ?? 0}
          onChange={(v) => mesclar("eixo", { posicao: v })}
          marks={{ "-40": "↓", 0: "0", 40: "↑" }}
          tooltip={{ formatter: (v) => (v > 0 ? `${v}% para cima` : v < 0 ? `${-v}% para baixo` : "No lugar") }}
        />
      </Campo>

      <Divider style={{ margin: "6px 0" }} />

      {/* ---------------- números ---------------- */}
      <Campo titulo="Números">
        <InputNumber
          size="small"
          style={{ width: 70 }}
          min={0}
          max={3}
          addonAfter="dec"
          value={valores.decimais}
          onChange={(v) => mesclar("valores", { decimais: v ?? 0 })}
        />
        <Input
          size="small"
          style={{ width: 76 }}
          placeholder="prefixo"
          value={valores.prefixo}
          onChange={(e) => mesclar("valores", { prefixo: e.target.value })}
        />
        <Input
          size="small"
          style={{ width: 76 }}
          placeholder="sufixo"
          value={valores.sufixo}
          onChange={(e) => mesclar("valores", { sufixo: e.target.value })}
        />
      </Campo>
      <Campo titulo="Mostrar valor em">
        <Select
          size="small"
          style={{ flex: 1 }}
          value={Array.isArray(valores.exibirEm) ? "todos" : valores.exibirEm || "todos"}
          options={OPCOES_ROTULO}
          onChange={(v) => mesclar("valores", { exibirEm: v })}
        />
      </Campo>

      <Campo titulo="Fundo do gráfico">
        <ColorPicker
          size="small"
          value={spec.fundo}
          presets={PRESETS}
          disabledAlpha
          onChange={(c) => onChange({ fundo: c.toHexString() })}
        />
        <Switch
          size="small"
          checked={spec.fundoPlot !== "nenhum"}
          onChange={(v) => onChange({ fundoPlot: v ? null : "nenhum" })}
        />
        <Text type="secondary" style={{ fontSize: 11 }}>
          parede atrás das barras
        </Text>
      </Campo>
    </Content>
  );
}
